import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { featureRows } from '@/assets/data/feaatureRows'
import Button from '@/components/Button'
import LabelWithTooltip from '@/components/LabelWithTooltip'

type Plan = {
  name: string
  color: string
  price: string
  yearly: string | JSX.Element
}

type Feature = {
  name: string
  tooltipTitle: string
  tooltipText: string
  availability: boolean[]
}

type ComparisonTableProps = {
  activePlanName: string
  selectedPlan: string
  activePlanInterval: string
  data: {
    id: string | number
    name: string
  }[]
  onStartFreeTrial: (planId: string | number, planName: string) => void
}

const plans: Plan[] = [
  {
    name: 'ShadeGuide AI',
    color: 'linear-gradient(90deg, #A0D911 0%, #5CDBD3 100%)',
    price: '€39/month',
    yearly: '€365/year',
  },
  {
    name: 'Micro Layering',
    color: 'linear-gradient(90deg, #FFC570 0%, #93D9FF 100%)',
    price: '€49/month',
    yearly: (
      <Typography>
        <span style={{ marginRight: 8 }}>€499/year</span>
        <span style={{ color: '#D1A4F1', fontWeight: 500 }}>€44/month</span>
      </Typography>
    ),
  },
  {
    name: 'Full Layering',
    color: 'linear-gradient(90deg, #FFA6A6 0%, #FFD990 100%)',
    price: '€79/month',
    yearly: (
      <Typography>
        <span style={{ marginRight: 8 }}>€799/year</span>
        <span style={{ color: '#D1A4F1', fontWeight: 500 }}>€66/month</span>
      </Typography>
    ),
  },
  {
    name: 'Premium',
    color: 'linear-gradient(90deg, #6FEEFF 0%, #C099FF 100%)',
    price: '€95/month',
    yearly: (
      <Typography>
        <span style={{ marginRight: 8 }}>€935/year</span>
        <span style={{ color: '#D1A4F1', fontWeight: 500 }}>€77/month</span>
      </Typography>
    ),
  },
]

const rowHeight = 70

export default function ComparisonTable({
  activePlanName,
  selectedPlan,
  activePlanInterval,
  data,
  onStartFreeTrial,
}: ComparisonTableProps) {
  const location = useLocation()
  const { t } = useTranslation('translation', { keyPrefix: 'plans' })
  const { t: translate } = useTranslation()
  return (
    <Box
      sx={{
        background: '#000',
        color: '#fff',
        py: 8,
        px: 4,
        maxWidth: '85.8125rem',
        margin: ' auto auto 5rem',
      }}
    >
      <Typography
        fontSize="36px"
        fontWeight={600}
        align="center"
        color="#B2DF8C"
      >
        {t('compareFeatures')}
      </Typography>

      <Grid container spacing={1} sx={{ mt: 4 }}>
        <Grid item xs={3}>
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: '#80DEEA',
                height: 121,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pl: 1,
                fontWeight: 'bold',
                borderBottom: '1px solid #444',
              }}
            >
              {t('features')}
            </Typography>
            {featureRows.map((feature: Feature) => (
              <Box
                key={feature.name}
                sx={{
                  borderBottom: '1px solid #444',
                  height: rowHeight,
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  color: '#fff',
                }}
              >
                <LabelWithTooltip
                  label={translate(feature.name)}
                  titleSx={{ fontSize: '1.125rem', fontWeight: 300 }}
                  tooltipText={
                    <Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        {translate(feature.tooltipTitle)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-line' }}
                      >
                        {translate(feature.tooltipText)}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ))}
          </Box>
        </Grid>

        {plans.map((plan, planIdx) => {
          const planName =
            plan.name === 'Premium' ? 'Matisse Premium' : plan.name

          const isCurrentPlan =
            planName === activePlanName && activePlanInterval === selectedPlan

          return (
            <Grid item xs={2.25} key={plan.name}>
              <Card
                sx={{
                  background: '#333',
                  borderRadius: '30px',
                  minHeight: '100%',
                }}
              >
                <CardContent
                  sx={{
                    height: 121,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      backgroundImage: plan.color,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      fontWeight: 'bold',
                    }}
                  >
                    {plan.name}
                  </Typography>

                  <Stack spacing={0.5} mt={1}>
                    <Typography>{plan.price}</Typography>
                    <Typography variant="body2">{plan.yearly}</Typography>
                  </Stack>
                </CardContent>

                {featureRows.map((feature: Feature, featIdx: number) => (
                  <Box
                    key={feature.name}
                    sx={{
                      borderTop: featIdx === 0 ? '1px solid #444' : 'none',
                      borderBottom: '1px solid #444',
                      height: rowHeight,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      px: 2,
                    }}
                  >
                    {feature.availability[planIdx] && (
                      <CheckCircleIcon
                        sx={{ color: '#64FFDA', fontSize: 26 }}
                      />
                    )}
                  </Box>
                ))}

                {data?.[planIdx] &&
                  location.pathname !== '/get-started/plans' && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderTop: '1px solid #444',
                        p: '34px 17px',
                      }}
                    >
                      <Button
                        label={
                          isCurrentPlan ? t('yourChoice') : t('selectPlan')
                        }
                        sx={{
                          bgcolor: '#FF47A0',
                          color: 'white',
                          width: '90%',
                        }}
                        disabled={isCurrentPlan}
                        onClick={() =>
                          onStartFreeTrial(data[planIdx].id, data[planIdx].name)
                        }
                      />
                    </Box>
                  )}
              </Card>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}
